import { fetchAllPvSize } from '../../services/allPvSizeService';
import { supabaseLocationService } from '../../services/supabaseLocationService';

interface SolarPVProjectCountData {
  chartData: any;
  chartOptions: any;
  narrative?: string;
  totalProjects?: number;
  latestYearProjects?: number;
}

// Helper function to get geofips for a location
async function getGeofipsForLocation(locationName: string): Promise<number | null> {
  try {
    return await supabaseLocationService.getGeofips(locationName);
  } catch (error) {
    console.error('Error getting geofips for location:', locationName, error);
    return null;
  }
}

export default async function fetchSolarPVProjectCountSupabase(location: string): Promise<SolarPVProjectCountData> {
  try {

    // Get geofips for the location
    const geofips = await getGeofipsForLocation(location);
    
    if (!geofips) {
      // Return placeholder data if no geofips found
      return {
        chartData: {
          labels: ['2019', '2020', '2021', '2022', '2023'],
          datasets: [{
            label: 'Residential Projects',
            data: [0, 0, 0, 0, 0],
            borderColor: '#1e3a8a',
            backgroundColor: 'rgba(30, 58, 138, 0.1)',
            fill: true
          }, {
            label: 'Commercial Projects',
            data: [0, 0, 0, 0, 0],
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: true
          }, {
            label: 'Utility Projects',
            data: [0, 0, 0, 0, 0],
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            fill: true
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Solar PV Projects by Sector - ${location} (No Geofips Found)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              position: 'top' as const,
              labels: {
                usePointStyle: true,
                padding: 20
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Year'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Number of Projects'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No geofips found for ${location} in the population database. This location may not be available in the solar installation database.`,
        totalProjects: 0,
        latestYearProjects: 0
      };
    }

    // Query Supabase by geoid for server-side filtering
    const { data: pvData, error } = await fetchAllPvSize({
      select: '*',
      geofips
    });

    if (error) {
      console.error('Error fetching Solar PV Project Count data:', error);
      throw error;
    }


    if (!pvData || pvData.length === 0) {
      // Return placeholder data if no records found
      return {
        chartData: {
          labels: ['2019', '2020', '2021', '2022', '2023'],
          datasets: [{
            label: 'Residential Projects',
            data: [0, 0, 0, 0, 0],
            borderColor: '#1e3a8a',
            backgroundColor: 'rgba(30, 58, 138, 0.1)',
            fill: true
          }, {
            label: 'Commercial Projects',
            data: [0, 0, 0, 0, 0],
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: true
          }, {
            label: 'Utility Projects',
            data: [0, 0, 0, 0, 0],
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            fill: true
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Solar PV Projects by Sector - ${location} (No Data Available)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              position: 'top' as const,
              labels: {
                usePointStyle: true,
                padding: 20
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Year'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Number of Projects'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No solar PV project data available for ${location}. This may indicate that the location has not yet been added to the solar installation database, or that solar installations are not yet recorded for this area.`,
        totalProjects: 0,
        latestYearProjects: 0
      };
    }

    // Process the data to create chart datasets (like the HTML version)
    const allYears = Array.from(new Set(pvData.map(item => parseInt(item.year)))).sort();
    const years = allYears.filter(year => year <= 2024); // Filter to 2024 and earlier like HTML
    const sectors = Array.from(new Set(pvData.map(item => item.sector))).filter(Boolean);
    
    const datasets = sectors.map((sector, index) => {
      const colors = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#06b6d4'];
      const color = colors[index % colors.length];
      
      const data = years.map(year => {
        const yearData = pvData.filter(item => 
          parseInt(item.year) === year && item.sector === sector
        );
        return yearData.reduce((sum, item) => sum + (item.num_projects || 0), 0); // Use num_projects column
      });

      return {
        label: sector,
        data: data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1,
        fill: true,
        tension: 0.1
      };
    });

    const chartData = {
      labels: years,
      datasets: datasets
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `Solar PV Projects by Sector - ${location}`,
          font: { size: 16, weight: 'bold' as const }
        },
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 20
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Year'
          }
        },
        y: {
          title: {
            display: true,
            text: 'Number of Projects'
          },
          beginAtZero: true
        }
      }
    };

    // Calculate project counts for narrative using the num_projects column
    const totalProjects = pvData.reduce((sum, item) => sum + (item.num_projects || 0), 0);
    const residentialProjects = pvData.filter(item => item.sector === 'residential').reduce((sum, item) => sum + (item.num_projects || 0), 0);
    const commercialProjects = pvData.filter(item => item.sector === 'commercial').reduce((sum, item) => sum + (item.num_projects || 0), 0);
    const utilityProjects = pvData.filter(item => item.sector === 'utility').reduce((sum, item) => sum + (item.num_projects || 0), 0);

    const latestYear = Math.max(...years);
    const latestYearProjects = pvData.filter(item => parseInt(item.year) === latestYear).reduce((sum, item) => sum + (item.num_projects || 0), 0);

    const narrative = `${location} has ${totalProjects} total solar PV projects recorded across all sectors. Residential installations lead with ${residentialProjects} projects, followed by ${commercialProjects} commercial projects and ${utilityProjects} utility-scale projects. In ${latestYear}, the most recent year with data, ${location} had ${latestYearProjects} new solar PV projects installed.`;

    return {
      chartData,
      chartOptions,
      narrative,
      totalProjects,
      latestYearProjects
    };

  } catch (error) {
    console.error('Error in fetchSolarPVProjectCountSupabase:', error);
    throw error;
  }
}
